import {
  createIdea,
  createAttachment,
  getCategories,
  getInitiatives,
} from "./services";
import { Logger, toastActions } from "@base-provider";
import { useDispatch } from "react-redux";
import React, { useEffect } from "react";

import SFDrawer from "../drawer";
import { SFGridContainer, SFGridItem, SFStack } from "../grid";
import SFButton from "../button";
import SFAsyncActionButton from "../button/asyncActionButton";
import SFTextField from "../textField";
import SFCheckbox from "../checkbox";
import SFSelect from "../select";
import Rating from "@mui/material/Rating";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";

import { styled } from "@mui/material/styles";

import html2canvas from "html2canvas";

import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SFTypography from "../typography";
import { SFLoadingSpinner } from "../..";

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconEmpty .MuiSvgIcon-root": {
    fill: theme.palette.action.disabled,
  },
}));

const customIcons = {
  1: {
    icon: (
      <SentimentVeryDissatisfiedIcon sx={{ fontSize: 40, fill: "#ff4136" }} />
    ),
    label: "Very Dissatisfied",
  },
  2: {
    icon: <SentimentDissatisfiedIcon sx={{ fontSize: 40, fill: "#f9e336" }} />,
    label: "Dissatisfied",
  },
  3: {
    icon: <SentimentSatisfiedIcon sx={{ fontSize: 40, fill: "#6bf436" }} />,
    label: "Neutral",
  },
  4: {
    icon: <SentimentSatisfiedAltIcon sx={{ fontSize: 40, fill: "#35ee9a" }} />,
    label: "Satisfied",
  },
  5: {
    icon: <SentimentVerySatisfiedIcon sx={{ fontSize: 40, fill: "#35a4e8" }} />,
    label: "Very Satisfied",
  },
};

const StyledRatingNumber = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconEmpty": {
    borderColor: theme.palette.action.disabled,
  },
  "& .MuiRating-iconEmpty .MuiSvgIcon-root": {
    fill: theme.palette.action.disabled,
  },
}));

// create an array of 10 color gradient ranging from red to green
const colorScale = [
  "#ff4136",
  "#ec5517",
  "#d66400",
  "#bf7000",
  "#a67900",
  "#8e7f00",
  "#758400",
  "#5b8600",
  "#3f871f",
  "#168736",
];

const SFTypographyRating = styled(SFTypography)(({ theme }) => ({
  color: theme.palette.action.disabled,
}));

const StyledRatingNumberItem = styled("div")(({ theme, value }) => ({
  border: `solid 2px ${colorScale[value - 1]}`,
  color: colorScale[value - 1],
  borderRadius: "50%",
  padding: "5px",
  width: 32,
  height: 32,
  marginRight: 5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
}));

function IconNumberContainer(props) {
  const { value, ...other } = props;
  return (
    <StyledRatingNumberItem value={value} {...other}>
      {value}
    </StyledRatingNumberItem>
  );
  //return <span {...other}>{customIcons[value].icon}</span>;
}

function IconContainer(props) {
  const { value, ...other } = props;
  return <span {...other}>{customIcons[value].icon}</span>;
}

const getScreenshot = () => {
  return new Promise((resolve, reject) => {
    html2canvas(document.body).then((canvas) => {
      resolve(canvas);
    });
  });
};

export const AhaIdeaPlugin = ({
  username,
  apiKey,
  projectCode,
  RenderItem,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);

  const [localState, setLocalState] = React.useState({
    idea: {
      name: "",
      description: "",
      initial_votes: 1,
      created_by: username,
      visibility: "public",
      categories: [],
      initiative: null,
      custom_fields: {
        sf_idea_rating: 0,
        sf_idea_recommendation: 0,
      },
    },
    open: false,
    submitting: false,
    categories: [],
    initiatives: [],
  });

  useEffect(() => {
    if (localState.open) {
      const getLookups = async () => {
        let categoriesRes = await getCategories(apiKey, projectCode);
        let initiativesRes = await getInitiatives(apiKey, projectCode);
        let categories = JSON.parse(categoriesRes.data);
        let initiatives = JSON.parse(initiativesRes.data);

        setLocalState({
          ...localState,
          categories: categories.idea_categories,
          initiatives: initiatives.initiatives,
        });
        setLoading(false);
      };
      getLookups();
    }
  }, [localState.open]);

  const { t } = useTranslation();

  const handleOpen = async () => {
    setLocalState({
      ...localState,
      initiative: null,
      idea: {
        name: "",
        description: "",
        initial_votes: 1,
        created_by: username,
        visibility: "public",
        categories: [],
        initiative: null,
        custom_fields: {
          sf_idea_rating: 0,
          sf_idea_recommendation: 0,
        },
      },
      open: true,
      submitting: false,
    });
  };

  const handleClose = () => {
    setLocalState({
      ...localState,
      initiative: null,
      idea: {
        name: "",
        description: "",
        initial_votes: 1,
        created_by: username,
        visibility: "public",
        categories: [],
        initiative: null,
        custom_fields: {
          sf_idea_rating: 0,
          sf_idea_recommendation: 0,
        },
      },
      open: false,
      submitting: false,
    });
  };

  const handleSubmit = async () => {
    if (
      localState.idea.initiative === null ||
      localState.idea.categories.length === 0 ||
      localState.idea.name === ""
    ) {
      dispatch(
        toastActions.showErrorMessage(
          t("components.formBuilder.messages.requiredFields")
        )
      );
      return;
    } else {
      setLocalState({
        ...localState,
        submitting: true,
      });
    }
    const res = await createIdea(apiKey, projectCode, {
      ...localState.idea,
    });
    const ideaData = JSON.parse(res.data);
    Logger.of("App.Core.AHA.handleSubmit.createIdea").info({ idea: ideaData });
    if (!localState.screenshot) {
      handleClose();
      return;
    }
    const screenshot = await getScreenshot();

    if (screenshot) {
      const blob = await new Promise((resolve) => screenshot.toBlob(resolve));
      const ss = await createAttachment(apiKey, projectCode, {
        note_id: ideaData.idea.description.id,
        screenshot: blob,
      });
      Logger.of("App.Core.AHA.handleSubmit.createAttachment").info({
        ss,
      });
      handleClose();
    }
  };

  return (
    <>
      <RenderItem onClick={handleOpen} />
      <SFDrawer
        size="m"
        open={localState.open}
        onClose={handleClose}
        ignoreHtml2Canvas
        header={t("components.aha.header")}
        footer={
          <SFStack direction="row" spacing={1} alignItems="center">
            <SFButton variant="secondary" onClick={handleClose}>
              {t("components.aha.cancelButton")}
            </SFButton>
            <SFAsyncActionButton variant="primary" onClick={handleSubmit}>
              {t("components.aha.submitButton")}
            </SFAsyncActionButton>
          </SFStack>
        }
      >
        {loading && (
          <SFGridContainer>
            <SFGridItem
              xs={12}
              sx={{ alignItems: "center", justifyContent: "center" }}
            >
              <SFLoadingSpinner />
            </SFGridItem>
          </SFGridContainer>
        )}
        {!loading && (
          <SFGridContainer>
            <SFGridItem xs={12}>
              <SFTypography variant="rightPanelLabel">
                {t("components.aha.satisfactionRating")}
              </SFTypography>
            </SFGridItem>
            <SFGridItem xs={12}>
              <StyledRating
                max={5}
                highlightSelectedOnly
                IconContainerComponent={IconContainer}
                getLabelText={(value) => customIcons[value].label}
                value={localState.idea.custom_fields.sf_idea_rating || 0}
                onChange={(event, newValue) =>
                  setLocalState({
                    ...localState,
                    idea: {
                      ...localState.idea,
                      custom_fields: {
                        ...localState.idea.custom_fields,
                        sf_idea_rating: newValue,
                      },
                    },
                  })
                }
              />
            </SFGridItem>
            <SFGridItem xs={12}>
              <SFSelect
                placeholder={t("components.aha.initiativePlaceholder")}
                selectedValue={localState.initiative}
                onChangeFilter={({ name, value }) =>
                  setLocalState({
                    ...localState,
                    initiative: value,
                    idea: {
                      ...localState.idea,
                      initiative: localState.initiatives.find(
                        (a) => a.name == value
                      ).id,
                    },
                  })
                }
                hasSearch={false}
                values={localState.initiatives.map((a) => a.name)}
                label={t("components.aha.initiativePlaceholder")}
                size="medium"
                required
              />
            </SFGridItem>
            <SFGridItem xs={12}>
              <SFSelect
                placeholder={t("components.aha.categoryPlaceholder")}
                selectedValue={localState.idea.category}
                onChangeFilter={({ name, value }) =>
                  setLocalState({
                    ...localState,
                    idea: {
                      ...localState.idea,
                      categories: [value],
                    },
                  })
                }
                hasSearch={false}
                values={localState.categories.map((a) => a.name)}
                label={t("components.aha.categoryPlaceholder")}
                size="medium"
                required
              />
            </SFGridItem>
            <SFGridItem xs={12}>
              <SFTextField
                variant="filled"
                label={t("components.aha.titlePlaceholder")}
                value={localState.idea.name}
                onChange={(e) =>
                  setLocalState({
                    ...localState,
                    idea: {
                      ...localState.idea,
                      name: e.target.value,
                    },
                  })
                }
                required
              />
            </SFGridItem>

            <SFGridItem xs={12}>
              <SFTextField
                multiline
                variant="filled"
                label={t("components.aha.detailsPlaceholder")}
                minRows={5}
                maxRows={20}
                value={localState.idea.description}
                onChange={(e) =>
                  setLocalState({
                    ...localState,
                    idea: {
                      ...localState.idea,
                      description: e.target.value,
                    },
                  })
                }
              />
            </SFGridItem>
            <SFGridItem xs={12}>
              <SFCheckbox
                key={0}
                label={t("components.aha.screenshotPlaceholder")}
                variant="body1"
                checked={localState.screenshot}
                onChange={(event, isChecked) =>
                  setLocalState({ ...localState, screenshot: isChecked })
                }
              />
              <SFTypography variant="body2">
                {t("components.aha.screenshotHint")}
              </SFTypography>
            </SFGridItem>
            <SFGridItem xs={12}>
              <SFTypography variant="rightPanelLabel">
                {t("components.aha.recommendationRating")}
              </SFTypography>
            </SFGridItem>
            <SFGridItem xs={12}>
              <div style={{ maxWidth: "363px" }}>
                <SFStack>
                  <StyledRatingNumber
                    max={10}
                    highlightSelectedOnly
                    IconContainerComponent={IconNumberContainer}
                    //getLabelText={(value) => customIcons[value].label}
                    value={
                      localState.idea.custom_fields.sf_idea_recommendation || 0
                    }
                    onChange={(event, newValue) =>
                      setLocalState({
                        ...localState,
                        idea: {
                          ...localState.idea,
                          custom_fields: {
                            ...localState.idea.custom_fields,
                            sf_idea_recommendation: newValue,
                          },
                        },
                      })
                    }
                  />
                  <SFStack direction="row" justifyContent="space-between">
                    <SFTypographyRating variant="body2">
                      {t("components.aha.recommendationMin")}
                    </SFTypographyRating>
                    <SFTypographyRating variant="body2">
                      {t("components.aha.recommendationMax")}
                    </SFTypographyRating>
                  </SFStack>
                </SFStack>
              </div>
            </SFGridItem>
          </SFGridContainer>
        )}
      </SFDrawer>
    </>
  );
};

AhaIdeaPlugin.propTypes = {
  apiKey: PropTypes.string.isRequired,
  projectCode: PropTypes.string.isRequired,
};
